/**
 * Two-way voice conversation loop (voice mode "speech").
 *
 * listen (Web Speech STT) → send transcript through the normal chat path
 * (useChatSend → ai-proxy) → synthesize the reply via /api/tts/conversation-synthesize
 * (ElevenLabs) → play → 500ms pause → reopen mic. Tap to interrupt playback.
 *
 * Never starts listening without an explicit user action (start/resume/interrupt).
 * Audio is a transient object URL, revoked after playback — never stored (S7).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useVoiceInput } from "@/hooks/use-voice-input";
import { getCurrentSession } from "@/lib/supabase";

export type ConversationPhase = "idle" | "listening" | "processing" | "speaking" | "paused" | "error";

const MIC_REOPEN_DELAY_MS = 500;
const SILENCE_AUTO_PAUSE_MS = 30_000;
const PROCESSING_TIMEOUT_MS = 120_000;
const MAX_CONSECUTIVE_ERRORS = 3;

interface AssistantMessageLike {
  id: string;
  content: string;
  status?: string;
  errorMessage?: string;
}

interface UseVoiceConversationOptions {
  /** True while voice mode is "speech". Deactivation stops everything. */
  active: boolean;
  isStreaming: boolean;
  lastAssistantMessage: AssistantMessageLike | null;
  /** Send user text through the normal chat send path. */
  onSendMessage: (text: string) => void;
  /** Called when the conversation ends itself (errors, Stop button). */
  onExit: () => void;
  onNotice?: (message: string) => void;
  ttsVoiceId: string | null;
  ttsSpeed: number;
}

export function useVoiceConversation({
  active,
  isStreaming,
  lastAssistantMessage,
  onSendMessage,
  onExit,
  onNotice,
  ttsVoiceId,
  ttsSpeed,
}: UseVoiceConversationOptions) {
  const [phase, setPhase] = useState<ConversationPhase>("idle");
  const [userTranscript, setUserTranscript] = useState("");
  const [assistantText, setAssistantText] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const activeRef = useRef(active);
  activeRef.current = active;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reopenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const finalChunksRef = useRef<string[]>([]);
  const streamObservedRef = useRef(false);
  const spokenMessageIdRef = useRef<string | null>(null);
  const onSendRef = useRef(onSendMessage);
  onSendRef.current = onSendMessage;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;

  const notice = useCallback((msg: string) => onNoticeRef.current?.(msg), []);

  const clearTimer = (ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const silenceEpochRef = useRef(0);
  const fatalSttErrorRef = useRef<string | null>(null);

  const voice = useVoiceInput({
    continuous: false,
    onResult: (text) => {
      finalChunksRef.current.push(text);
      // Speech arrived — reset the silence auto-pause window.
      silenceEpochRef.current = Date.now();
    },
    onError: (message) => {
      // "No speech detected" is handled by the empty-transcript path on end.
      if (/no speech/i.test(message)) return;
      // Permission/network problems can't be retried silently — exit speech mode.
      fatalSttErrorRef.current = message;
    },
  });
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  const beginListening = useCallback(
    ({ keepSilenceEpoch = false }: { keepSilenceEpoch?: boolean } = {}) => {
      if (!activeRef.current) return;
      if (!voiceRef.current.isSupported) {
        setErrorMessage("Voice recognition is not supported in this browser.");
        setPhase("error");
        return;
      }
      stopAudio();
      finalChunksRef.current = [];
      setUserTranscript("");
      if (!keepSilenceEpoch) silenceEpochRef.current = Date.now();
      setPhase("listening");
      voiceRef.current.startListening();
      // Backstop for recognition sessions that never end on their own.
      clearTimer(silenceTimerRef);
      silenceTimerRef.current = setTimeout(() => {
        if (phaseRef.current === "listening" && activeRef.current) {
          voiceRef.current.stopListening();
        }
      }, SILENCE_AUTO_PAUSE_MS + 5_000);
    },
    [stopAudio]
  );

  const scheduleMicReopen = useCallback(() => {
    clearTimer(reopenTimerRef);
    reopenTimerRef.current = setTimeout(() => {
      if (activeRef.current && phaseRef.current === "speaking") beginListening();
    }, MIC_REOPEN_DELAY_MS);
  }, [beginListening]);

  const registerError = useCallback(
    (message: string, { reopen }: { reopen: boolean }) => {
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
        notice("Voice conversation ended due to errors.");
        setErrorMessage(message);
        setPhase("error");
        onExitRef.current();
        return;
      }
      notice(message);
      if (reopen && activeRef.current) beginListening();
      else setPhase("paused");
    },
    [beginListening, notice]
  );

  const speak = useCallback(
    async (text: string) => {
      setPhase("speaking");
      try {
        const session = await getCurrentSession();
        if (!session?.access_token) throw new Error("no-session");
        const res = await fetch("/api/tts/conversation-synthesize", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text, voiceId: ttsVoiceId ?? undefined, speed: ttsSpeed }),
        });
        if (!res.ok) throw new Error(`tts-${res.status}`);
        const blob = await res.blob();
        if (!activeRef.current || phaseRef.current !== "speaking") return;
        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          consecutiveErrorsRef.current = 0;
          stopAudio();
          scheduleMicReopen();
        };
        audio.onerror = () => {
          stopAudio();
          registerError("Audio playback failed. The reply is shown as text.", { reopen: true });
        };
        await audio.play();
      } catch {
        // Graceful degradation: reply text stays visible; reopen the mic.
        stopAudio();
        if (activeRef.current) {
          consecutiveErrorsRef.current = 0; // a failed TTS with visible text is not a hard error loop
          notice("Voice playback unavailable — reply shown as text.");
          scheduleMicReopen();
        }
      }
    },
    [notice, registerError, scheduleMicReopen, stopAudio, ttsSpeed, ttsVoiceId]
  );

  // Listening ended (silence or recognition end): send the transcript, or retry.
  const prevIsListeningRef = useRef(false);
  useEffect(() => {
    const was = prevIsListeningRef.current;
    prevIsListeningRef.current = voice.isListening;
    if (!was || voice.isListening) return;
    if (phaseRef.current !== "listening" || !activeRef.current) return;

    clearTimer(silenceTimerRef);

    // Permission/network STT failures end the conversation (can't retry silently).
    if (fatalSttErrorRef.current) {
      const message = fatalSttErrorRef.current;
      fatalSttErrorRef.current = null;
      setErrorMessage(message);
      setPhase("error");
      notice("Voice recognition unavailable.");
      onExitRef.current();
      return;
    }

    const text = finalChunksRef.current.join(" ").trim();
    finalChunksRef.current = [];
    if (!text) {
      // Silence is not an error: retry until the 30s window lapses, then pause.
      if (Date.now() - silenceEpochRef.current >= SILENCE_AUTO_PAUSE_MS) {
        setPhase("paused");
        notice("Conversation paused. Tap to resume.");
      } else {
        notice("Didn't catch that. Try again.");
        beginListening({ keepSilenceEpoch: true });
      }
      return;
    }
    consecutiveErrorsRef.current = 0;
    setUserTranscript(text);
    setAssistantText("");
    spokenMessageIdRef.current = null;
    streamObservedRef.current = false;
    setPhase("processing");
    onSendRef.current(text);
    clearTimer(processingTimerRef);
    processingTimerRef.current = setTimeout(() => {
      if (phaseRef.current === "processing" && activeRef.current) {
        registerError("The AI took too long to respond.", { reopen: false });
      }
    }, PROCESSING_TIMEOUT_MS);
  }, [voice.isListening, registerError, beginListening, notice]);

  // AI response finished streaming → speak it.
  useEffect(() => {
    if (phaseRef.current !== "processing" || !activeRef.current) return;
    if (isStreaming) {
      streamObservedRef.current = true;
      return;
    }
    if (!streamObservedRef.current) return;
    const message = lastAssistantMessage;
    if (!message || spokenMessageIdRef.current === message.id) return;
    clearTimer(processingTimerRef);
    spokenMessageIdRef.current = message.id;

    if (message.status === "error") {
      setErrorMessage(message.errorMessage || "The AI request failed.");
      setPhase("error");
      notice("Voice conversation ended: the AI request failed.");
      onExitRef.current();
      return;
    }
    const text = typeof message.content === "string" ? message.content.trim() : "";
    if (!text) {
      registerError("Empty response received.", { reopen: true });
      return;
    }
    setAssistantText(text);
    void speak(text);
  }, [isStreaming, lastAssistantMessage, notice, registerError, speak]);

  const start = useCallback(() => {
    consecutiveErrorsRef.current = 0;
    setErrorMessage(null);
    setAssistantText("");
    beginListening();
  }, [beginListening]);

  const pause = useCallback(() => {
    clearTimer(silenceTimerRef);
    clearTimer(reopenTimerRef);
    voiceRef.current.stopListening();
    stopAudio();
    setPhase("paused");
  }, [stopAudio]);

  const resume = useCallback(() => {
    setErrorMessage(null);
    beginListening();
  }, [beginListening]);

  /** Tap during playback: cut the AI off and listen immediately. */
  const interrupt = useCallback(() => {
    if (phaseRef.current !== "speaking") return;
    clearTimer(reopenTimerRef);
    stopAudio();
    notice("Interrupted");
    beginListening();
  }, [beginListening, notice, stopAudio]);

  const stop = useCallback(() => {
    clearTimer(silenceTimerRef);
    clearTimer(processingTimerRef);
    clearTimer(reopenTimerRef);
    voiceRef.current.stopListening();
    stopAudio();
    setPhase("idle");
    setUserTranscript("");
    setAssistantText("");
    setErrorMessage(null);
    onExitRef.current();
  }, [stopAudio]);

  // Deactivation (mode toggled away, unmount): halt everything quietly.
  useEffect(() => {
    if (active) return;
    clearTimer(silenceTimerRef);
    clearTimer(processingTimerRef);
    clearTimer(reopenTimerRef);
    voiceRef.current.stopListening();
    stopAudio();
    setPhase("idle");
  }, [active, stopAudio]);

  useEffect(() => {
    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
      if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
      stopAudio();
    };
  }, [stopAudio]);

  const listeningText =
    phase === "listening"
      ? [voice.transcript, voice.interimTranscript].filter(Boolean).join(" ").trim()
      : userTranscript;

  return {
    phase,
    isSupported: voice.isSupported,
    /** Live transcription while listening; the sent utterance afterwards. */
    userText: listeningText,
    assistantText,
    errorMessage,
    start,
    pause,
    resume,
    interrupt,
    stop,
  };
}
